// Every page's head tags (title, description, canonical URL, whether it's
// indexed), and the pages for what isn't there. Nothing here touches the
// DOM.

import type { AppResponse, AppsResponse, CompanyResponse, TagResponse } from "../../shared/api.ts";
import { COMPANY_ROLES } from "../../shared/companies.ts";
import { COMPANY_MIN_GAMES, TAGS_PATH, appPath, companyPath, tagPagePath } from "../../shared/site.ts";
import { date, number } from "./figures.ts";
import { type PagedList, backLinkHtml, externalLink, pageHref, pageLabel, plural } from "./markup.ts";
import {
  type CompanyPageRef,
  SEARCH_PATH,
  SITE,
  type SearchQuery,
  listFound,
  searchHref,
  storeUrl
} from "./routes.ts";
import type { TagPageRef } from "./tag-pages.ts";

// A page's head tags, as headTags takes them: its title, description and
// path, an image, whether it's indexed, and its trail from the home page
// (`crumbs`, [name, path] pairs) for the Worker's JSON-LD.
export interface PageMeta {
  title: string;
  description: string;
  path: string;
  image?: string | null;
  index?: boolean;
  crumbs?: [string, string][] | null;
}

// A page's head tags as headTags gives them: [attribute, key, content].
interface HeadTags {
  title: string;
  canonical: string;
  meta: [string, string, string | null][];
}

// A page's <head> tags, for both writers: head.ts's setMeta sets them in the
// document, the Worker's writePage writes them into index.html. `meta` is
// listMeta's or appMeta's: title, description and path, and optionally image
// and index: false. The canonical URL drops ?range and anything else that
// only changes the view, so a game's ranges don't compete in search results.
// A null content means the page has no such tag.
export function headTags({ title, description, path, image, index = true }: PageMeta): HeadTags {
  const url = SITE + path;
  // No image for an empty one too, so og:image and twitter:card agree.
  image ||= null;

  return {
    title,
    canonical: url,
    meta: [
      ["name", "description", description],
      ["name", "robots", index ? null : "noindex"],
      ["property", "og:title", title],
      ["property", "og:description", description],
      ["property", "og:url", url],
      ["property", "og:image", image],
      ["name", "twitter:card", image ? "summary_large_image" : "summary"]
    ]
  };
}

const NOT_FOUND_TITLE = "Not found · steamdata.dev";

// " · Page N"-style text for a later page, `text(n)` given its number as
// readers see it (pageLabel); nothing on page 0.
const pageSuffix = (page: number, text: (n: number) => string): string =>
  page === 0 ? "" : text(pageLabel(page));

// A paged list's head tags, as head.ts's setMeta takes them, for page `page`
// of the list at `path`: `title(page)` and `description(page)`, or for a page
// past the end (`list` given and not found), a "not found" page that isn't
// indexed. Without `list` (not loaded yet), the page's own. `crumbs` is its
// trail from the home page, as [name, path] pairs, for the Worker's JSON-LD.
export function pagedMeta(
  path: string,
  page: number,
  list: PagedList<unknown> | null,
  title: (page: number) => string,
  description: (page: number) => string,
  crumbs?: [string, string][] | null
): PageMeta {
  const href = pageHref(page, path);

  if (list && !listFound(list)) {
    return {
      title: NOT_FOUND_TITLE,
      description: `There's no page ${pageLabel(page)}.`,
      path: href,
      index: false
    };
  }

  return {
    path: href,
    title: title(page),
    description: description(page),
    ...(crumbs !== undefined && { crumbs })
  };
}

// A list page's head tags (see pagedMeta).
export const listMeta = (page: number, list: AppsResponse | null = null): PageMeta =>
  pagedMeta(
    "/",
    page,
    list,
    (p) => `${pageSuffix(p, (n) => `Page ${n} · `)}steamdata.dev · Steam player counts`,
    (p) =>
      "Live and historical concurrent player counts for Steam games, recorded every hour: 24h change, all-time peaks and charts with update markers." +
      pageSuffix(p, (n) => ` Page ${n}.`)
  );

// A tag page's head tags (see pagedMeta).
export const tagMeta = ({ tag, value, def }: TagPageRef, page: number, list: TagResponse | null = null): PageMeta =>
  pagedMeta(
    tagPagePath(tag, value),
    page,
    list,
    (p) => `${def.title}${pageSuffix(p, (n) => ` · Page ${n}`)} · steamdata.dev`,
    (p) => def.text + pageSuffix(p, (n) => ` Page ${n}.`),
    [["Tags", TAGS_PATH], [def.title, tagPagePath(tag, value)]]
  );

// The tags index's head tags.
export const tagsMeta: PageMeta = {
  title: "Tags · steamdata.dev",
  description:
    "Steam games grouped by how their player counts are moving (top risers, fallers, climbers, near their peak, bumped by an update), new releases and when they're played.",
  path: TAGS_PATH
};

// The search page's head tags. Not indexed: every query would be its own
// thin page. Its URL keeps the query, so a shared link previews as that
// search.
export const searchMeta = (query: SearchQuery): PageMeta => ({
  title: `${query.q ? `${query.q} · ` : ""}Search · steamdata.dev`,
  description: query.q
    ? `Tracked Steam games matching “${query.q}”, with their player counts.`
    : "Search tracked Steam games by name and by tag.",
  path: searchHref(query, query.page),
  index: false
});

// A game page's head tags while its game loads: the id is all that's known.
// Not indexed until the game's own replace them.
export const appLoadingMeta = (appId: string | number): PageMeta => ({
  title: `App ${appId} · steamdata.dev`,
  description: "Steam player count, recorded every hour.",
  path: appPath(appId),
  index: false
});

// The head tags that write figures: a game page's and a company page's.
// A game page's head tags, as headTags takes them (see listMeta).
export function appMeta(game: AppResponse, name: string): PageMeta {
  const path = appPath(game.app_id);
  return {
    title: appTitle(game, name),
    description: appDescription(game, name),
    path,
    image: game.header_image,
    crumbs: [[name, path]]
  };
}

// The count is in the title because it's what the page is searched for; not
// for a game no longer tracked, whose last count isn't who's playing.
function appTitle(game: AppResponse, name: string): string {
  const playing =
    game.active && game.player_count != null
      ? `: ${number.format(game.player_count)} playing on Steam`
      : "";

  return `${name} player count${playing} · steamdata.dev`;
}

// A game page's search snippet: the figures it would be searched for, in words.
function appDescription(game: AppResponse, name: string): string {
  let text = `${name} concurrent player count on Steam, recorded every hour.`;

  if (game.player_count != null) {
    text += ` Latest: ${number.format(game.player_count)} players.`;
  }
  if (game.peak_player_count != null && game.peak_recorded_at != null) {
    text += ` All-time peak: ${number.format(game.peak_player_count)} (${date(
      game.peak_recorded_at
    )}).`;
  }

  return text + " Charts from 24 hours to all time, with update markers.";
}

// A company page's head tags (see pagedMeta), from its GET
// /api/companies response, `list`; while it loads, the slug stands in for
// the name. Indexed with COMPANY_MIN_GAMES games or more.
export function companyMeta({ role, slug }: CompanyPageRef, page: number, list: CompanyResponse | null = null): PageMeta {
  const { label, verb } = COMPANY_ROLES[role];
  // Until the name's known, the role alone, not the slug standing in for it.
  const by = list ? ` by ${list.name}` : "";
  const path = companyPath(role, slug);
  const meta = pagedMeta(
    path,
    page,
    list,
    (p) =>
      `${list ? `Games ${verb}${by}` : label}${pageSuffix(p, (n) => ` · Page ${n}`)} · steamdata.dev`,
    (p) =>
      `Steam player counts for the ${list ? `${number.format(list.total)} ` : ""}tracked ${
        plural(list?.total ?? 0, "game")
      } ${verb}${by}, most played first, recorded every hour.` +
      (list?.ranked ? ` ${number.format(list.players)} playing them at the latest count.` : "") +
      pageSuffix(p, (n) => ` Page ${n}.`),
    // Read once the list is in, by the Worker's JSON-LD.
    list && [[`${list.name} (${label})`, path]]
  );

  return list && list.total < COMPANY_MIN_GAMES ? { ...meta, index: false } : meta;
}

// ---- Not found ----------------------------------------------------------

// A page that isn't there, for both writers: app.js shows it for any path it
// doesn't route; the Worker, as a real 404, for those under /apps/ it can't
// make a game's page of.
export const notFoundMeta = (path: string): PageMeta => ({
  title: NOT_FOUND_TITLE,
  description: "Page not found.",
  path,
  index: false
});

// A "not found" page's content, one design for every kind: its heading,
// what's missing (`text`, HTML) and links on from there.
const missingPage = (heading: string, text: string, links: string[]): string => `
    ${backLinkHtml()}
    <h1>${heading}</h1>
    <div class="panel status">
      <p>${text}</p>
      <p>${links.join(" · ")}</p>
    </div>`;

const searchLink = `<a href="${SEARCH_PATH}" data-nav>Search tracked games</a>`;

export const notFoundPage = missingPage("Page not found", "There's nothing at this address.", [
  searchLink,
  `<a href="${TAGS_PATH}" data-nav>Browse the tags</a>`
]);

// A game page for an id that isn't tracked, as headTags takes it; not indexed,
// and a 404 from the Worker.
export const appNotFoundMeta = (appId: string | number): PageMeta => ({
  title: NOT_FOUND_TITLE,
  description: "That game isn't tracked.",
  path: appPath(appId),
  index: false
});

// Its content: the id may be a game that isn't tracked yet, so it says how
// games get tracked and links to the store page the id would have.
export const appNotFoundPage = (appId: string | number): string =>
  missingPage(
    "Game not tracked",
    `No tracked game has app id ${appId}. Games are added once they reach
      Steam's top 100 by players.`,
    [
      searchLink,
      externalLink(storeUrl(appId), "Steam store page"),
      `<a href="/about" data-nav>Which games are tracked</a>`
    ]
  );
